import { ConfigurationItemArrayEditor } from '@client/components/_core/configuration/ConfigurationItemArrayEditor';
import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Form, Row, Select, Space } from 'antd';
import { Categories } from './Categories';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/mspr/definitions')({
    component: Definitions,
    staticData: {
        ui: {
            useParentTitle: true,
        },
    },
});

function Definitions() {
    const { trpc, trpcUtils } = useTrpc();
    const orgId = useOrgId();

    const { data: orgTypes } = trpc.mspr.orgType.listOrgTypes.useQuery();
    const { mutate: updateOrg } = trpc.org.updateOrg.useMutation();
    const { data: org } = trpc.org.getOrg.useQuery({ orgId });

    const handleUpdateOrg = (msprOrgTypeId: bigint) => {
        modal.confirm({
            title: 'Confirm Organisation Type Change',
            content: `Changing the Organisation Type will update the default list of Minimum Critical Operations and Categories for your organisation. Data on existing MSP records will remain unchanged`,
            onOk: () => {
                message.loading('Updating organisation type...');
                updateOrg(
                    {
                        set: { msprOrgTypeId },
                    },
                    {
                        onSuccess: () => {
                            trpcUtils.org.invalidate();
                            trpcUtils.mspr.invalidate();
                            message.success('Organisation type updated successfully');
                        },
                        onError: (error) => {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    return (
        <PageController route={Route}>
            <Space orientation="vertical" className="w-full" size="large">
                <Card>
                    <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} className="w-[600px]">
                        <Form.Item label="Organisation Type">
                            <Select<bigint, { label: string; value: bigint }>
                                placeholder="Select organisation type"
                                value={org?.msprOrgTypeId}
                                options={orgTypes?.map((orgType) => ({ label: orgType.name, value: orgType.id })) || []}
                                onChange={handleUpdateOrg}
                            />
                        </Form.Item>
                    </Form>
                </Card>
                <Categories />
                <Row gutter={16}>
                    <Col span={12}>
                        <ConfigurationItemArrayEditor
                            title="Rating"
                            settingToEdit="MSPR_RATINGS"
                            description="Rename, add or remove a rating."
                            itemRules={[{ required: true, message: 'Field is required' }]}
                        />
                    </Col>
                    <Col span={12}>
                        <ConfigurationItemArrayEditor
                            title="Statuses"
                            settingToEdit="MSPR_STATUSES"
                            description="Rename, add or remove a status."
                            itemRules={[{ required: true, message: 'Field is required' }]}
                        />
                    </Col>
                </Row>
                <Outlet />
            </Space>
        </PageController>
    );
}
